import { memo } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { CustomTooltip } from 'src/components/CustomTooltip';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { pluralize } from 'src/lib/format';

import { useStyles } from './useStyles';

const checkValidity = (count, min, max) => {
  if (min != null && count < min) {
    return false;
  }
  if (max > 0 && count > max) {
    return false;
  }

  return true;
};

export const TextAndWordCounter = memo(
  ({
    className,
    charsCount,
    minChars,
    maxChars,
    wordsCount,
    minWords,
    maxWords,
    hint,
    displayMaxChars,
    displayMaxWords,
    strict,
  }) => {
    const styles = useStyles();

    const charsValid = strict ? checkValidity(charsCount, minChars, maxChars) : true;
    const wordsValid = strict ? checkValidity(wordsCount, minWords, maxWords) : true;

    return (
      <CustomTooltip title={hint || ''}>
        <CustomTypography
          className={cx(styles.root, className)}
          variant={TYPOGRAPHY_VARIANT.regular14}
          component="span"
          data-testid="textAndWordCounter"
        >
          <span
            className={cx({
              [styles.invalid]: !charsValid,
            })}
          >
            {charsCount.toLocaleString()}
            {displayMaxChars && maxChars > 0 && <span>/{maxChars}</span>}{' '}
            {pluralize(charsCount, 'character', 'characters')}
          </span>

          <span> | </span>

          <span
            className={cx({
              [styles.invalid]: !wordsValid,
            })}
          >
            {wordsCount.toLocaleString()}
            {displayMaxWords && maxWords > 0 && <span>/{maxWords}</span>}{' '}
            {pluralize(wordsCount, 'word', 'words')}
          </span>
        </CustomTypography>
      </CustomTooltip>
    );
  }
);

TextAndWordCounter.propTypes = {
  className: PropTypes.string,
  charsCount: PropTypes.number,
  minChars: PropTypes.number,
  maxChars: PropTypes.number,
  wordsCount: PropTypes.number,
  minWords: PropTypes.number,
  maxWords: PropTypes.number,
  hint: PropTypes.string,
  strict: PropTypes.bool,
  displayMaxChars: PropTypes.bool,
  displayMaxWords: PropTypes.bool,
};
TextAndWordCounter.defaultProps = {
  className: null,
  charsCount: 0,
  minChars: 0,
  maxChars: 0,
  wordsCount: 0,
  minWords: 0,
  maxWords: 0,
  hint: null,
  strict: false,
  displayMaxChars: false,
  displayMaxWords: false,
};
